<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Process Payment</h1>
      <p class="subtitle">Enter a payment amount to apply to receivables. Payments will allocate to this receivable first, then to other receivables by due date.</p>
    </div>
    <div class="modal-card-body">
      <div v-if="isLoadingReceivables" class="loading-state">
        <div class="spinner"></div>
        <p>Loading receivables...</p>
      </div>
      <div v-else class="form-grid">
        <div class="amount-info">
          <div class="amount-info-item">
            <span class="amount-label">Recommended</span>
            <span class="amount-value">{{ formatUtils.formatCurrency(recommendedAmount) }}</span>
          </div>
          <div class="amount-info-item">
            <span class="amount-label">Maximum</span>
            <span class="amount-value">{{ formatUtils.formatCurrency(maximumAmount) }}</span>
          </div>
        </div>

        <div class="form-row">
          <label for="amount" class="form-label">Amount</label>
          <MonetaryInput v-model="amountCentsPaid" />
          <p v-if="allocationCount > 0" class="allocation-hint">
            Will apply to {{ allocationCount }} receivable{{ allocationCount !== 1 ? 's' : '' }}
          </p>
        </div>

        <div class="form-row">
          <label for="paymentMethod" class="form-label">Payment Method</label>
          <SelectInput v-model="paymentMethod" :options="paymentMethodOptions" placeholder="Select payment method..." />
        </div>

        <div class="form-row">
          <label for="datePaid" class="form-label">Date Paid</label>
          <DateInput v-model="datePaid" />
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click" @click="handleCancel">
        Cancel
      </button>
      <button class="button primary form-button" @click="handleSubmit" :disabled="isSubmitting || isLoadingReceivables">
        {{ isSubmitting ? 'Processing...' : 'Process Payment' }}
      </button>
    </div>
    <div v-if="error" class="modal-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import MonetaryInput from '@/components/inputs/MonetaryInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as formatUtils from '@/utils/formatters'
import * as api from '@/api'
import { getLocalToday } from '@/utils/dateUtils'

export default {
  name: "CreatePaymentModal",
  props: {
    receivable: Object
  },
  emits: ['payment-created'],
  data() {
    return {
      formatUtils,
      amountCentsPaid: 0,
      paymentMethod: '',
      datePaid: getLocalToday(),
      isSubmitting: false,
      isLoadingReceivables: false,
      error: null,
      allReceivables: [],
      paymentMethodOptions: [
        { value: 'cash', label: 'Cash' },
        { value: 'check', label: 'Check' },
        { value: 'mastercard', label: 'Mastercard' },
        { value: 'visa', label: 'Visa' },
        { value: 'amex', label: 'AMX' },
        { value: 'discover', label: 'Discover' },
        { value: 'cashiers_check', label: 'Cashiers Check' },
        { value: 'agms', label: 'AGMS' },
        { value: 'hsa', label: 'HSA' },
        { value: 'ach', label: 'ACH' },
        { value: 'finance', label: 'Finance' }
      ]
    }
  },
  computed: {
    recommendedAmount() {
      return this.receivable?.amountCentsBalance || 0;
    },
    maximumAmount() {
      return this.allReceivables.reduce((sum, r) => sum + (r.amountCentsBalance || 0), 0);
    },
    allocationCount() {
      if (!this.amountCentsPaid || this.amountCentsPaid <= 0) return 0;

      let remaining = this.amountCentsPaid;
      let count = 0;

      // Count the selected receivable first
      const selectedReceivable = this.receivable;
      const selectedBalance = selectedReceivable?.amountCentsBalance || 0;

      if (selectedBalance > 0 && remaining > 0) {
        count++;
        remaining -= selectedBalance;
      }

      // Count other receivables by due date
      for (const receivable of this.allReceivables) {
        // Skip the selected receivable (already counted)
        if (receivable.receivableId === selectedReceivable?.receivableId) {
          continue;
        }

        if (remaining <= 0) break;
        if (receivable.amountCentsBalance > 0) {
          count++;
          remaining -= receivable.amountCentsBalance;
        }
      }

      return count;
    }
  },
  async created() {
    await this.loadReceivables();
  },
  methods: {
    async loadReceivables() {
      try {
        this.isLoadingReceivables = true;
        const response = await api.fetchReceivables({
          ticketId: this.receivable.ticketId,
          limit: 1000
        });

        // Sort by date due (earliest first) and filter out fully paid receivables
        this.allReceivables = (response.receivables || [])
          .filter(r => r.amountCentsBalance > 0)
          .sort((a, b) => new Date(a.dateDue) - new Date(b.dateDue));

      } catch (err) {
        console.error('Failed to fetch receivables:', err);
        this.error = 'Failed to load receivables: ' + err.message;
        this.allReceivables = [];
      } finally {
        this.isLoadingReceivables = false;
      }
    },
    handleCancel() {
      // Close modal via close-on-click class
    },
    calculateAllocations() {
      const allocations = [];
      let remaining = this.amountCentsPaid;

      // Step 1: Prioritize the selected receivable first
      const selectedReceivable = this.receivable;
      const selectedBalance = selectedReceivable.amountCentsBalance;

      if (selectedBalance > 0 && remaining > 0) {
        const amountToAllocate = Math.min(remaining, selectedBalance);
        allocations.push({
          receivableId: selectedReceivable.receivableId,
          amountCentsPaid: amountToAllocate
        });
        remaining -= amountToAllocate;
      }

      // Step 2: Allocate excess to other receivables by due date
      for (const receivable of this.allReceivables) {
        // Skip the selected receivable (already processed)
        if (receivable.receivableId === selectedReceivable.receivableId) {
          continue;
        }

        if (remaining <= 0) break;

        const balance = receivable.amountCentsBalance;
        if (balance > 0) {
          const amountToAllocate = Math.min(remaining, balance);
          allocations.push({
            receivableId: receivable.receivableId,
            amountCentsPaid: amountToAllocate
          });
          remaining -= amountToAllocate;
        }
      }

      return allocations;
    },
    async handleSubmit() {
      this.error = null;

      if (!this.amountCentsPaid || this.amountCentsPaid <= 0) {
        this.error = 'Please enter a valid payment amount';
        return;
      }

      if (!this.paymentMethod) {
        this.error = 'Please select a payment method';
        return;
      }

      if (!this.datePaid) {
        this.error = 'Please enter a date paid';
        return;
      }

      if (this.amountCentsPaid > this.maximumAmount) {
        this.error = 'Payment amount cannot exceed the maximum balance (' + formatUtils.formatCurrency(this.maximumAmount) + ')';
        return;
      }

      this.isSubmitting = true;

      try {
        const allocations = this.calculateAllocations();

        const paymentData = {
          ticketId: this.receivable.ticketId,
          patientId: this.receivable.patientId,
          amountCentsPaid: this.amountCentsPaid,
          paymentMethod: this.paymentMethod,
          datePaid: this.datePaid,
          allocations
        };

        const payment = await api.createPayment(paymentData);

        this.$emit('payment-created', payment);

        const closeButton = this.$el.querySelector('.close-on-click');
        if (closeButton) {
          closeButton.click();
        }
      } catch (err) {
        this.error = err.message || 'Failed to process payment';
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  components: { CloseIcon, MonetaryInput, SelectInput, DateInput }
};
</script>

<style scoped>
.modal-card {
  max-width: 480px;
}

.subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  font-weight: 400;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f0f0f0;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #6b7280;
  font-size: 14px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.amount-info {
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: var(--border-radius);
  border: 1px solid #e5e7eb;
}

.amount-info-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.amount-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-value {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.allocation-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  font-style: italic;
}

.modal-card-footer {
  display: flex;
  gap: 16px;
}
</style>
